/**
 * Where the window should scroll after a navigation.
 *
 * Kept free of the router instance and the DOM so it can be tested in the node
 * test environment; `scrollBehavior` in index.ts reads the header height and
 * forwards both to it.
 *
 * Order matters:
 *
 * 1. A saved position wins. It only exists on back and forward, and there the
 *    citizen expects to return to where they were, even if the URL carries a
 *    hash, which is what the browser itself does for ordinary pages.
 * 2. A hash scrolls to that section. Vue Router scrolls with window.scrollTo,
 *    which ignores CSS `scroll-margin-top`, so the sticky header height has to
 *    be passed in as `headerOffset` or the section heading slides under it.
 *    The welcome header wraps on narrow screens, so the caller reads its live
 *    height rather than a constant.
 * 3. Anything else starts at the top, as every page did before.
 */

/** Used when the live header height is not available. */
export const STICKY_HEADER_OFFSET = 80;

export interface SavedPosition {
  left: number;
  top: number;
}

export type ScrollTarget =
  | SavedPosition
  | { top: number }
  | { el: string; top: number; behavior: "smooth" };

export function scrollTarget(
  to: { hash: string },
  savedPosition: SavedPosition | null | undefined,
  headerOffset = 0
): ScrollTarget {
  if (savedPosition) return savedPosition;
  if (to.hash) {
    return { el: to.hash, top: headerOffset, behavior: "smooth" };
  }
  return { top: 0 };
}
